import { OverridableComponent } from "@material-ui/core/OverridableComponent";
import { SvgIconTypeMap } from "@material-ui/core";

import { AdGroupTemplate, AdGroupType } from "./adGroupTypes";

// API request payload types (Frontend)

export type UpdatedCampaignBody = {
  name: string;
  description: string;
  dailyBudget: number;
  campaign_budget: number;
  maxBid: number;
  startDate: Date;
  endDate: Date;
  active: boolean;
  maxCompanies: number;
  showInHubspot: boolean;
  isVideoCampaign: boolean;
  archived: boolean;
};

export type ArchiveCampaignBody = {
  archived: boolean;
};

export type StatusCampaignBody = {
  active: boolean;
};

// API request payload types (Backend)

export type UpdatedCampaignPayload = {
  name: string;
  description: string;
  campaign_budget: number;
  max_bid: number;
  start_date: Date;
  end_date: Date;
  active: boolean;
  maxCompanies: number;
  showInHubspot: boolean;
  isVideoCampaign: boolean;
  archived: boolean;
};

export type CampaignLinteItemType = {
  active: boolean;
  fromFile: string | null;
  hubspotCompanyId: number;
  beeswaxAdvertiserId: number;
  beeswaxCampaignId: number;
  beeswaxLineItemId: number;
  brandgenCampaignId: string;
  createdAt: string;
  lineItemReports?: ReportType[];
  line_item_budget: number;
  line_item_name: string;
  line_item_spend: number;
  updatedAt: string;
};

export type ReportType = {
  brandgenCampaignReportId: string;
  brandgenCampaignId: string;
  timeFrame: string;
  beeswaxCampaignId: number;
  beeswaxAdvertiserId: number;
  clicks: number;
  cpc_usd: number;
  cpm_usd: number;
  ctr: number;
  impressions: number;
  spend: number;
  conversions: number;
  click_through_conversions: number;
  spend_per_conversion: number;
  viewthrough_conversions: number;
  video_companion_clicks: number;
  video_companion_ctr: number;
  video_companion_views: number;
  video_complete_percent: number;
  video_completes: number;
  video_midpoints: number;
  video_plays: number;
  video_q1: number;
  video_q3: number;
  video_skips: number;
  average_viewable_seconds: number;
  invalid_impression_percent: number;
  invalid_impressions: number;
  measurable_impression_percent: number;
  measurable_impressions: number;
  measurable_invalid_impressions: number;
  vcpm: number;
  viewability_percent: number;
  viewability_measurability_percent: number;
  viewable_impressions: number;
  viewable_seconds: number;
  conversion_orders: number;
  conversions_value: number;
  postback_conversions: number;
  reach: number;
};

export type CampaignType = {
  brandgenCampaignId: string;
  beeswaxCampaignId: number;
  beeswaxAdvertiserId: number;
  createdBy: string;
  name: string;
  description: string;
  active: boolean;
  start_date: string;
  end_date: string;
  daily_budget: number;
  campaign_budget: number;
  pixel_event_id: number;
  burnRate: number;
  accounts: number;
  brandgenAdvertiserId: string;
  billed_amount: number;
  showInHubspot: boolean;
  targeting_info: TargetingInfo;
  creatives?: Array<number>;
  maxBid: number;
  isVideoCampaign: boolean;
  maxCompanies: number;
  archived: boolean;
  agencyId: string;
  campaignReports?: Array<ReportType>;
  lineItems?: Array<any>;
};

export type TargetingInfo = {
  domain_list: Array<number>;
  geo_country: Array<string>;
  geo_state: Array<string>;
  geo_zip: Array<string>;
  role_title_segments: Array<string>;
  retargeting_segments: Array<string>;
};

export type TargetingSelections = {
  domain_list: Array<BrandgenDomainList>;
  geo_country: Array<string>;
  geo_state: Array<GeoState>;
  geo_zip: Array<string>;
  role_title_segments: Array<RolesTitlesHolder>;
  retargeting_segments: Array<string>;
};

export type GeoState = {
  country_code_2: string;
  country_code_3: string;
  key: string;
  region_code: string;
  region_name: string;
};

export type BrandgenDomainList = {
  domainListId: string;
  beeswaxListId: number;
  brandgenAdvertiserId: string;
  name: string;
  domains: Array<BrandgenDomainListItem>;
};

export type BrandgenDomainListItem = {
  active: boolean;
  beeswaxListId: number;
  beeswaxListItemId: number;
  brandgenItemId: string;
  createdAt: string;
  domain: string;
  domainListId: string;
  updatedAt: string;
};

export type RolesTitlesHolder = {
  role: string;
  cookieSets: TitlesHolder[];
};

export type TitlesHolder = {
  segment_key: string;
  title: string;
};

export enum ReportTimeFrames {
  ALL_TIME = "ALL_TIME",
  DAYS_7 = "7_DAYS",
  DAYS_30 = "30_DAYS",
  DAYS_90 = "90_DAYS",
}

interface BaseCreativeAsset {
  height: number | null;
  width: number | null;
  fileType: "image" | "video";
  duration?: number;
}

export interface CreativeAssetFromFile extends BaseCreativeAsset {
  file: File;
}

export interface CreativeAssetFromTemplate extends BaseCreativeAsset {
  sourceUrl: string;
  name: string;
  size: number;
  mimetype: string;
}

export type CreativeAssetToCreate = CreativeAssetFromFile | CreativeAssetFromTemplate;

export type AdGroupToCreateType = {
  name: string;
  description: string;
  adGroupType: AdGroupType;
  creativeAssets: CreativeAssetToCreate[];
};

export type SelectedAdGroupTemplate = {
  adGroup: AdGroupTemplate;
  creatives: CreativeAssetToCreate[];
};

export type TierValueType = {
  description: string;
  displayOrder: number;
  hidden: boolean;
  label: string;
  value: string;
};

export type CampaignCompanySortValuesType = {
  tier_values: TierValueType[];
};

export type BrandgenCampaignTargetingType = {
  domains: string[][];
  segments?: string[];
  blogKeywords?: string[];
  blogTopics?: string[];
  geoRegions?: Array<any>;
};

export type BeeswaxCampaignCreateType = {
  advertiser_id: number;
  campaign_name: string;
  campaign_budget: number | string;
  budget_type: number | string;
  start_date: Date;
  end_date?: Date;
  active: boolean;
};

export type BrandgenCampaignCreateType = {
  beeswaxCampaign: BeeswaxCampaignCreateType;
  targetingInfo: BrandgenCampaignTargetingType;
  brandgenCampaignId: string;
  brandgenAdvertiserId: string;
  agencyId: string;
  createdBy: string;
  name: string;
  description: string;
  creative: number[];
};

export type CampaignBasicDetailsType = {
  brandgenCampaignId: string;
  agencyId: string;
  createdBy: string;
  name: string;
  description: string;
  startDate: Date | string;
  endDate: Date | string;
  estimatedBudget: number | string;
  dailyBudget: number | string;
  maxBid: number | string;
  showInHubspot: boolean;
  maxCompanies: number | string;
  isVideoCampaign: boolean;
};

export type FoundCompanyInfo = {
  cookieDate: Array<any>;
  cookieInfo: boolean;
  domain: string;
  domainInfo: boolean;
  name: string;
};

export type FoundCompaniesInfo = {
  companies: Array<FoundCompanyInfo> | undefined;
  kickfire: Array<any> | undefined;
  cookies: Array<any> | undefined;
  matches: number;
};

export type FoundHubspotPages = {
  pages: Array<any>;
  blogPosts: Array<any>;
};

export type FoundHubspotPage = {
  id: number;
  name: string;
  url: string;
  pageType: "BLOG" | "LANDING" | "WEB";
  state: string;
};

export type CookieSet = {
  segment_key: string;
  title: string;
};

export type RoleSet = {
  role: string;
  cookieSets: CookieSet[];
};

export type RoleTitleGroup = {
  roleType: string;
  roleSet: RoleSet[];
};

export type GeoTargetOption = {
  country_code_2: string;
  country_code_3: string;
  key: string;
  region_code: string;
  region_name: string;
};

export type Domain = {
  active: boolean;
  beeswaxListId: number;
  beeswaxListItemId: number;
  brandgenItemId: string;
  createdAt: string;
  domain: string;
  domainListId: string;
  updatedAt: string;
};

export type PublicDomainList = {
  beeswaxListId: number;
  createdAt: string;
  domainListId: string;
  name: string;
  publicPublications: Domain[];
};

export type CustomDomainList = {
  beeswaxListId: number;
  brandgenAdvertiserId: string;
  createdAt: string;
  domainListId: string;
  name: string;
  updatedAt: string;
  domains: Domain[];
};

export type BeeswaxCampaignImage = {
  creative_asset_name: string;
  height: string;
  width: string;
  creative_asset_id: number;
  displayAdUrl: string;
};

export type CookieState = Record<string, CookieSet[]>;

export type CampaignStepperSlideType = {
  component: JSX.Element;
  title: string;
  reduxAction: (...args: any) => void;
  icon?: OverridableComponent<SvgIconTypeMap<{}, "svg">>;
  name: string;
};

export enum CampaignPageTypes {
  landingPage = "landing_page",
  websitePage = "site_page",
  blogPosts = "blogPosts",
}

export enum CampaignMediaTypeEnum {
  VIDEO = "video",
  DISPLAY = "display",
}

export const CUSTOM_SLIDE_NAMES = {
  KEYWORD: "keywords",
  GEOTARGET: "geographyTarget",
  ZIP_CODE_TARGET: "zipCodeTarget",
  DOMAIN_LIST: "domainList",
  CUSTOM_COMPANIES: "customCompanies",
  CUSTOM_ROLETITLE: "customRoleTitle",
};

export const MANAGED_SLIDE_NAMES = {
  COMPANIES: "companies",
  TITLES: "titles",
};

export const DEFAULT_SLIDE_NAMES = {
  ADVERTISER_SELECT: "advertiserSelect",
  BASIC_DETAILS: "basicDetails",
  ...MANAGED_SLIDE_NAMES,
  PAGES: "pages",
  AD_GROUPS: "adGroups",
  SUMMARY: "summary",
  PIXEL: "pixel",
};
